import { useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import AssetAutocomplete from "components/holdings/AssetAutocomplete";
import {
  BucketNumberEnum,
  useListBucketsQuery,
} from "features/trading/buckets";
import {
  HeldPeriod,
  serializeHeldPeriods,
} from "features/trading/heldPeriods";
import { BucketActions } from "./useBucketActions";

// Bucket 1 and Bucket 2 never share a security: each one's picker marks
// what the other holds. The server refuses the add; the mark says why first.
const EXCLUSIVE_BUCKET: Partial<Record<BucketNumberEnum, BucketNumberEnum>> = {
  [BucketNumberEnum.BUCKET_1]: BucketNumberEnum.BUCKET_2,
  [BucketNumberEnum.BUCKET_2]: BucketNumberEnum.BUCKET_1,
};

interface BucketAddAssetRowProps {
  accountId: number;
  // The bucket being added to.
  number: BucketNumberEnum;
  actions: Pick<BucketActions, "addAsset" | "isAddingAsset">;
  maxWidth?: number;
  // The report's periods, when this row is opened from the report settings.
  // Absent on the client page's Buckets tab, which has no reporting period.
  heldPeriods?: HeldPeriod[];
}

/**
 * Autocomplete + Add button for Buckets 1/2. The picker is restricted to
 * the securities this bucket's account held — over `heldPeriods` when they
 * are given, otherwise what it holds now. Shared between the Buckets tab
 * and the edit modal.
 */
export const BucketAddAssetRow = ({
  accountId,
  number,
  actions,
  maxWidth,
  heldPeriods,
}: BucketAddAssetRowProps) => {
  const [assetToAdd, setAssetToAdd] = useState<number | "">("");
  const otherNumber = EXCLUSIVE_BUCKET[number];

  // The same arguments the caller's own bucket list uses (the report
  // settings send the periods, the Buckets tab does not), so this reads
  // the list already in the cache instead of fetching another.
  const heldPeriodsParam = serializeHeldPeriods(heldPeriods);
  const { data: bucketsData } = useListBucketsQuery(
    {
      account: accountId,
      ...(heldPeriodsParam ? { held_periods: heldPeriodsParam } : {}),
    },
    { skip: otherNumber === undefined },
  );
  const otherBucketAssetIds = useMemo(
    () =>
      new Set(
        bucketsData?.results
          .find((bucket) => bucket.number === otherNumber)
          ?.assets.map((member) => member.asset) ?? [],
      ),
    [bucketsData, otherNumber],
  );

  const handleAdd = async () => {
    if (assetToAdd === "") return;
    const ok = await actions.addAsset(assetToAdd);
    if (ok) setAssetToAdd("");
  };

  return (
    <Box display="flex" gap={1} alignItems="center">
      <Box flexGrow={1} maxWidth={maxWidth}>
        <AssetAutocomplete
          value={assetToAdd}
          onChange={setAssetToAdd}
          heldByAccountId={accountId}
          heldPeriods={heldPeriods}
          getOptionNote={(asset) =>
            otherBucketAssetIds.has(asset.id)
              ? `In Bucket ${otherNumber}`
              : undefined
          }
          label="Add asset"
          // Kept short: the modal's field clips a longer line mid-word.
          placeholder={
            heldPeriods?.length
              ? "Held in this account during the period"
              : "Currently held in this account"
          }
        />
      </Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        disabled={assetToAdd === "" || actions.isAddingAsset}
        onClick={handleAdd}
      >
        Add
      </Button>
    </Box>
  );
};
